import { useState, type FormEvent } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Mail, Lock, KeyRound, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Two-stage login form.
 *
 * Stage A — email + password. If the server replies with a normal
 * TokenResponse we're done; if it replies with `requires_2fa: true`,
 * we stash the temp_token and switch to Stage B.
 *
 * Stage B — 6-digit TOTP / backup code. The "Use a backup code"
 * link just relaxes the input mask; the server accepts either by
 * verifying TOTP first and falling back to the hashed backup-code
 * list. Submitting calls `complete2FA(temp_token, code)`.
 */
export function LoginForm() {
  const { login, complete2FA } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Stage B state.
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [backupMode, setBackupMode] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (!tempToken) {
        const res = await login(email, password, rememberMe);
        if (res && res.requires_2fa) {
          setTempToken(res.temp_token);
          setCode("");
          setBackupMode(false);
        }
        // res === undefined → fully logged in; AuthContext routed user.
      } else {
        if (!code.trim()) {
          setError("Enter your authentication code");
          return;
        }
        await complete2FA(tempToken, code.trim());
      }
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      setError(
        axiosError.response?.data?.detail ||
          "Sign-in failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const cancel2FA = () => {
    setTempToken(null);
    setCode("");
    setBackupMode(false);
    setError("");
  };

  if (tempToken) {
    return (
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="rounded-lg bg-accent/5 border border-accent/20 px-4 py-3 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-accent shrink-0 mt-0.5" />
          <div className="text-sm text-text-primary">
            <p className="font-medium">Enter your 6-digit authentication code</p>
            <p className="text-xs text-text-muted mt-1">
              {backupMode
                ? "Enter one of your saved backup codes."
                : "Open your authenticator app and enter the current code."}
            </p>
          </div>
        </div>

        <Input
          label={backupMode ? "Backup code" : "Authentication code"}
          type="text"
          value={code}
          onChange={(e) =>
            setCode(
              backupMode
                ? e.target.value.toUpperCase().slice(0, 16)
                : e.target.value.replace(/\D/g, "").slice(0, 6),
            )
          }
          placeholder={backupMode ? "ABCD1234" : "123456"}
          icon={<KeyRound className="h-4 w-4" />}
          inputMode={backupMode ? "text" : "numeric"}
          autoComplete="one-time-code"
          required
          autoFocus
        />

        <button
          type="button"
          onClick={() => {
            setBackupMode(!backupMode);
            setCode("");
          }}
          className="text-sm text-accent hover:underline"
        >
          {backupMode
            ? "Use a 6-digit code instead"
            : "Use a backup code instead"}
        </button>

        {error && (
          <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-3">
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={cancel2FA}
            className="text-sm text-text-muted hover:text-text-primary"
          >
            Back
          </button>
          <Button type="submit" isLoading={isLoading}>
            Verify &amp; sign in
          </Button>
        </div>

        <p className="text-xs text-text-muted">
          Having trouble?{" "}
          <Link to="/help" className="text-accent hover:underline">
            Contact your administrator
          </Link>
          .
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        icon={<Mail className="h-4 w-4" />}
        required
        autoFocus
      />

      <Input
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Enter your password"
        icon={<Lock className="h-4 w-4" />}
        required
      />

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="rounded border-border text-accent focus:ring-accent"
          />
          Remember me
        </label>
        <Link
          to="/forgot-password"
          className="text-sm text-accent hover:text-accent-600 transition-colors"
        >
          Forgot password?
        </Link>
      </div>

      {error && (
        <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <Button type="submit" isLoading={isLoading} className="w-full">
        Sign in
      </Button>
    </form>
  );
}
